import React, { useState } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../../components/annuaire/designSystem';
import LigneBonusRepar from '../../components/annuaire/shared/LigneBonusRepar';
import { erreursCoordonnees } from './coordonnees';
import { horaireLong, majuscule } from './creneaux';
import {
  BoutonVert,
  Corps,
  Encart,
  LARGE,
  Pied,
  SousTitre,
  Titre,
  mobile,
} from './ui';

/**
 * Étape 3 sur `/reserver/:slug` : les coordonnées, à la place du compte
 * (maquette validée le 23/09, `widget-etape-coordonnees.html`). Le client n'a
 * pas de compte Roulez Jeunesse et n'en crée pas ici : l'atelier le prévient
 * par email à chaque décision.
 *
 * Les quatre champs sont obligatoires. L'erreur d'un champ apparaît quand on le
 * quitte, ou pour tous d'un coup si l'on essaie d'envoyer : la demande ne part
 * pas tant qu'il en reste une.
 */

const Rangee = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0 12px;

  @media (min-width: 520px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Champ = styled.div`
  margin-bottom: 14px;

  label {
    display: block;
    margin-bottom: 6px;
    font-size: 13.5px;
    font-weight: 600;
    color: ${colors.textDark};
  }
  label i {
    font-style: normal;
    color: ${colors.textSecondary};
  }
  input {
    box-sizing: border-box;
    width: 100%;
    min-height: 50px;
    padding: 0 14px;
    border: 2px solid ${(p) => (p.$erreur ? '#c0391b' : colors.borderLight)};
    border-radius: 14px;
    background: ${colors.white};
    font-family: ${fonts.body};
    /* 16px minimum : en dessous, iOS zoome à la mise au point. */
    font-size: 16px;
    color: ${colors.textDark};
    outline: none;
  }
  input:focus {
    border-color: ${(p) => (p.$erreur ? '#c0391b' : colors.textDark)};
  }
  p {
    margin: 6px 0 0;
    font-size: 12.5px;
    line-height: 1.4;
    color: ${colors.textSecondary};
  }
  p.erreur {
    color: #a52f16;
  }
`;

/** Sur téléphone seulement : au-dessus de 900 px, la colonne de droite le dit déjà. */
const Recap = styled.dl`
  margin: 6px 0 0;
  padding: 4px 14px;
  border-radius: 16px;
  background: ${colors.grisClair};

  @media (min-width: ${LARGE}) {
    display: none;
  }

  div {
    display: flex;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid ${mobile.border};
    font-size: 13.5px;
    line-height: 1.4;
  }
  div:last-child {
    border-bottom: none;
  }
  dt {
    flex: 0 0 96px;
    font-weight: 600;
    color: ${colors.textSecondary};
  }
  dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

const CHAMPS = [
  {
    cle: 'prenom',
    libelle: 'Prénom',
    type: 'text',
    autoComplete: 'given-name',
  },
  { cle: 'nom', libelle: 'Nom', type: 'text', autoComplete: 'family-name' },
  {
    cle: 'tel',
    libelle: 'Téléphone',
    type: 'tel',
    autoComplete: 'tel',
    inputMode: 'tel',
    aide: 'L’atelier vous appelle s’il a besoin d’une précision.',
  },
  {
    cle: 'email',
    libelle: 'Email',
    type: 'email',
    autoComplete: 'email',
    inputMode: 'email',
  },
];

export default function EtapeCoordonnees({
  atelier,
  bonus = null,
  instant,
  valeurs,
  onChange,
  erreursApi,
  adresse,
  probleme,
  selected,
  envoiEnCours,
  erreur,
  onEnvoyer,
  titreRef,
}) {
  const [vus, setVus] = useState({});
  const [tenteEnvoi, setTenteEnvoi] = useState(false);

  const locales = erreursCoordonnees(valeurs);
  // Une erreur de l'api reste affichée jusqu'à ce que le client retouche le champ.
  const message = (cle) =>
    (tenteEnvoi || vus[cle]) && locales[cle]
      ? locales[cle]
      : (erreursApi && erreursApi[cle]) || null;

  const envoyer = () => {
    setTenteEnvoi(true);
    const premier = CHAMPS.find(({ cle }) => locales[cle]);
    if (premier) {
      const el = document.getElementById(`coordonnees-${premier.cle}`);
      if (el) el.focus();
      return;
    }
    onEnvoyer();
  };

  return (
    <>
      <Corps>
        <Titre tabIndex={-1} ref={titreRef}>
          Vos coordonnées
        </Titre>
        <SousTitre>
          {instant
            ? `Pour réserver chez ${atelier}. Nous vous envoyons ensuite un code par email, pour confirmer que c’est bien vous. Aucun compte n’est créé.`
            : `Pour envoyer votre demande à ${atelier}. Vous êtes prévenu par email à chaque étape, aucun compte n’est créé.`}
        </SousTitre>
        {erreur && (
          <Encart $ton="alerte" role="alert">
            {erreur}
          </Encart>
        )}
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            envoyer();
          }}>
          <Rangee>
            {CHAMPS.slice(0, 2).map((c) => (
              <ChampCoordonnee
                key={c.cle}
                champ={c}
                valeur={valeurs[c.cle]}
                message={message(c.cle)}
                onChange={onChange}
                onQuitter={() => setVus((v) => ({ ...v, [c.cle]: true }))}
              />
            ))}
          </Rangee>
          {CHAMPS.slice(2).map((c) => (
            <ChampCoordonnee
              key={c.cle}
              champ={c}
              valeur={valeurs[c.cle]}
              message={message(c.cle)}
              onChange={onChange}
              onQuitter={() => setVus((v) => ({ ...v, [c.cle]: true }))}
            />
          ))}
        </form>
        <Recap>
          <div>
            <dt>Adresse</dt>
            <dd>{adresse}</dd>
          </div>
          <div>
            <dt>Votre problème</dt>
            <dd>{probleme}</dd>
          </div>
          <div>
            <dt>{instant ? 'Horaire' : 'Créneaux'}</dt>
            <dd>
              {instant && selected[0]
                ? majuscule(horaireLong(selected[0]))
                : `${selected.length} créneau${
                    selected.length > 1 ? 'x' : ''
                  } proposé${selected.length > 1 ? 's' : ''}`}
            </dd>
          </div>
          <LigneBonusRepar paliers={bonus} />
        </Recap>
      </Corps>
      <Pied>
        <BoutonVert type="button" disabled={envoiEnCours} onClick={envoyer}>
          {envoiEnCours
            ? instant
              ? 'Envoi du code…'
              : 'Envoi en cours…'
            : instant
            ? 'Recevoir mon code par email'
            : 'Envoyer ma demande'}
        </BoutonVert>
      </Pied>
    </>
  );
}

function ChampCoordonnee({ champ, valeur, message, onChange, onQuitter }) {
  const id = `coordonnees-${champ.cle}`;
  return (
    <Champ $erreur={!!message}>
      <label htmlFor={id}>
        {champ.libelle} <i aria-hidden="true">*</i>
      </label>
      <input
        id={id}
        name={champ.cle}
        type={champ.type}
        inputMode={champ.inputMode}
        autoComplete={champ.autoComplete}
        required
        aria-required="true"
        aria-invalid={!!message}
        aria-describedby={message || champ.aide ? `${id}-aide` : undefined}
        value={valeur}
        onChange={(e) => onChange(champ.cle, e.target.value)}
        onBlur={onQuitter}
      />
      {message ? (
        <p id={`${id}-aide`} className="erreur" role="alert">
          {message}
        </p>
      ) : (
        champ.aide && <p id={`${id}-aide`}>{champ.aide}</p>
      )}
    </Champ>
  );
}
